import { useMemo } from 'react';
import { MODULE_ALIASES } from '../constants/modules';

interface Permission {
  module: string;
  can_view: boolean;
  can_add: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

interface User {
  id: string | number;
  email: string;
  name: string;
  permissions?: Permission[] | string[] | null;
  role?: string;
}

// Helper function to safely check if permissions is an array of strings
const isStringArray = (arr: any[]): arr is string[] => {
  return arr.length > 0 ? typeof arr[0] === 'string' : true;
};

// Helper function to safely check if permissions is an array of Permission objects
const isPermissionArray = (arr: any[]): arr is Permission[] => {
  return arr.length > 0 ? 
    typeof arr[0] === 'object' && 
    arr[0] !== null &&
    'module' in arr[0] && 
    'can_view' in arr[0] : true;
};

// Helper function to validate user data structure
const isValidUser = (userData: any): userData is User => {
  return (
    userData && 
    typeof userData === 'object' &&
    'id' in userData &&
    'email' in userData &&
    'name' in userData
  );
};

// Helper function to normalize module names for consistent checking
// Some modules use hyphens in frontend but underscores in backend
const normalizeModuleName = (module: string): string => {
  // First convert to lowercase
  const lowercase = module.toLowerCase();
  
  // Check if we have a specific alias for this module
  if (MODULE_ALIASES[lowercase]) {
    console.log(`Module alias found for ${lowercase} → ${MODULE_ALIASES[lowercase]}`);
    return MODULE_ALIASES[lowercase];
  }
  
  // No alias found, just return the lowercase version
  console.log(`No module alias found for ${lowercase}, using as is`);
  return lowercase;
};

export const usePermissions = () => {
  const user = useMemo(() => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        console.log('usePermissions: No user data found in localStorage');
        return null;
      }
      
      const userData = JSON.parse(userStr);
      
      // Validate basic user structure
      if (!isValidUser(userData)) {
        console.error('usePermissions: Invalid user data structure in localStorage');
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        return null;
      }
      
      // Debug log
      console.log('usePermissions: Loading user data from localStorage:', {
        id: userData.id,
        name: userData.name,
        role: userData.role || 'undefined',
        permissions: userData.permissions ? 
          `Array with ${userData.permissions.length} items` : 
          'undefined',
        rawPermissions: userData.permissions // Log the raw permissions data
      });
      
      // Validate permissions data
      if (!userData.permissions) {
        console.warn('usePermissions: User data missing permissions, initializing as empty array');
        userData.permissions = [];
      } else if (!Array.isArray(userData.permissions)) {
        console.warn('usePermissions: User permissions is not an array, initializing as empty array');
        userData.permissions = [];
      } else {
        // Log permissions format
        if (userData.permissions.length > 0) {
          if (isStringArray(userData.permissions)) {
            console.log('usePermissions: User has string permissions format:', userData.permissions);
          } else if (isPermissionArray(userData.permissions)) {
            console.log('usePermissions: User has object permissions format with modules:', 
              userData.permissions.map((p: Permission) => p.module).join(', '));
          } else {
            console.warn('usePermissions: Unknown permissions format, resetting to empty array');
            userData.permissions = [];
          }
        } else {
          console.log('usePermissions: User has empty permissions array');
        }
      }
      
      return userData;
    } catch (error) {
      console.error('Error parsing user data from localStorage:', error);
      // Clear invalid data
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      return null;
    }
  }, []);

  const hasPermission = (module: string, action: 'view' | 'add' | 'edit' | 'delete'): boolean => {
    console.log(`usePermissions.hasPermission: Checking ${action} permission for module ${module}`);

    if (!user) {
      console.log(`usePermissions.hasPermission: No user found, denying access to ${module}`);
      return false;
    }

    console.log(`usePermissions.hasPermission: User role is ${user.role}`);

    // MASTER role bypasses all permission checks
    if (user.role === 'MASTER') return true;

    // Handle missing permissions property
    if (!user.permissions) {
      console.log(`usePermissions.hasPermission: User has no permissions property, denying access to ${module}`);
      return false;
    }

    // Safely check if permissions is an array
    if (!Array.isArray(user.permissions)) {
      console.warn(`usePermissions.hasPermission: permissions is not an array: ${typeof user.permissions}`);
      return false;
    }
    
    console.log(`usePermissions.hasPermission: permissions array length: ${user.permissions.length}`);
    
    // Empty array check
    if (user.permissions.length === 0) {
      console.log(`usePermissions.hasPermission: Empty permissions array, denying access to ${module}`);
      return false;
    }

    // Normalize the module name for checking
    const normalizedModule = normalizeModuleName(module);

    // Check if permissions is an array of strings (old format)
    if (isStringArray(user.permissions)) {
      // Old format: permissions is an array of module names
      // Check if the normalized module name is in the array, also normalizing array entries
      const hasAccess = user.permissions.some(p => normalizeModuleName(p) === normalizedModule);
      console.log(`usePermissions.hasPermission: Using legacy string format - ${module} access: ${hasAccess}`);
      return hasAccess;
    }
    
    // Check if permissions is an array of Permission objects (new format)
    if (isPermissionArray(user.permissions)) {
      // New format: permissions is an array of objects with module and actions
      // Find the permission by matching normalized module names
      const permission = user.permissions.find(p => normalizeModuleName(p.module) === normalizedModule);
      console.log(`usePermissions.hasPermission: Permissions for module ${module}: ${permission ? 'found' : 'not found'}`);
      
      if (!permission) {
        console.log(`usePermissions.hasPermission: Module ${module} not found in permissions`);
        console.log(`usePermissions.hasPermission: Available modules: ${user.permissions.map(p => p.module).join(', ')}`);
        return false;
      }

      // Check for the specific action
      let result = false;
      switch (action) {
        case 'view':
          result = permission.can_view;
          break;
        case 'add':
          result = permission.can_add;
          break;
        case 'edit':
          result = permission.can_edit;
          break;
        case 'delete':
          result = permission.can_delete;
          break;
        default:
          result = false;
      }
      console.log(`usePermissions.hasPermission: Module ${module}, action ${action}, result: ${result}`);
      return result;
    }
    
    // If we reach here, the permissions array is in an unknown format
    console.warn(`usePermissions.hasPermission: Unknown permissions format for module ${module}`);
    return false;
  };

  const canView = (module: string) => hasPermission(module, 'view');
  const canAdd = (module: string) => hasPermission(module, 'add');
  const canEdit = (module: string) => hasPermission(module, 'edit');
  const canDelete = (module: string) => hasPermission(module, 'delete');
  
  // New function to check if user has any permission for a module
  const hasAnyPermission = (module: string): boolean => {
    console.log(`usePermissions.hasAnyPermission: Checking if user has any permission for module ${module}`);

    if (!user) {
      console.log(`usePermissions.hasAnyPermission: No user found, denying access to ${module}`);
      return false;
    }

    // MASTER role bypasses all permission checks
    if (user.role === 'MASTER') return true;

    // Handle missing permissions property
    if (!user.permissions) {
      console.log(`usePermissions.hasAnyPermission: User has no permissions property, denying access to ${module}`);
      return false;
    }
    
    // Safely check if permissions is an array
    if (!Array.isArray(user.permissions)) {
      console.warn(`usePermissions.hasAnyPermission: permissions is not an array: ${typeof user.permissions}`);
      return false;
    }
    
    // Empty array check
    if (user.permissions.length === 0) {
      console.log(`usePermissions.hasAnyPermission: Empty permissions array, denying access to ${module}`);
      return false;
    }
    
    // Normalize the module name for checking
    const normalizedModule = normalizeModuleName(module);
    
    // Check if permissions is an array of strings (old format)
    if (isStringArray(user.permissions)) {
      // Old format: permissions is an array of module names
      // Check if the normalized module name is in the array, also normalizing array entries
      const hasAccess = user.permissions.some(p => normalizeModuleName(p) === normalizedModule);
      console.log(`usePermissions.hasAnyPermission: Using legacy string format - ${module} access: ${hasAccess}`);
      return hasAccess;
    }
    
    // Check if permissions is an array of Permission objects (new format)
    if (isPermissionArray(user.permissions)) {
      // New format: permissions is an array of objects with module and actions
      // Find the permission by matching normalized module names
      const permission = user.permissions.find(p => normalizeModuleName(p.module) === normalizedModule);
      console.log(`usePermissions.hasAnyPermission: Permissions for module ${module}: ${permission ? 'found' : 'not found'}`);
      
      if (!permission) {
        console.log(`usePermissions.hasAnyPermission: Module ${module} not found in permissions`);
        return false;
      }
      
      // Check if user has any permission (view, add, edit, or delete)
      const hasAny = permission.can_view || permission.can_add || permission.can_edit || permission.can_delete;
      console.log(`usePermissions.hasAnyPermission: Module ${module}, has any permission: ${hasAny}`);
      return hasAny;
    }
    
    // If we reach here, the permissions array is in an unknown format
    console.warn(`usePermissions.hasAnyPermission: Unknown permissions format for module ${module}`);
    return false;
  };

  return {
    user,
    hasPermission,
    canView,
    canAdd,
    canEdit,
    canDelete,
    hasAnyPermission
  };
}; 